import React, { useEffect, useRef, useState } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';

export interface SearchableComboboxOption {
  id: string;
  label: string;
}

interface DescribedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  describedBy?: string;
}

const DescribedInput = React.forwardRef<HTMLInputElement, DescribedInputProps>(
  ({ describedBy, ...props }, ref) => (
    <input {...props} ref={ref} aria-describedby={describedBy} />
  ),
);

interface SearchableComboboxProps {
  id: string;
  label: string;
  options: SearchableComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
}

const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  invalid,
  describedBy,
  disabled,
}) => {
  const selectedOption = options.find(option => option.id === value) ?? null;
  const selectedLabel = selectedOption?.label ?? '';
  const [query, setQuery] = useState(selectedLabel);
  const selectedOnClose = useRef(false);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visible = normalizedQuery
    ? options.filter(option => option.label.toLocaleLowerCase().includes(normalizedQuery))
    : options;

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: SearchableComboboxOption | null) => {
        if (!option) return;
        onChange(option.id);
        selectedOnClose.current = true;
        setQuery(option.label);
      }}
      onClose={() => {
        if (selectedOnClose.current) {
          selectedOnClose.current = false;
          return;
        }
        setQuery(selectedLabel);
      }}
      disabled={disabled}
      by="id"
      immediate
    >
      <div className="relative w-full">
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
        </label>
        <ComboboxInput
          as={DescribedInput}
          id={id}
          value={query}
          onChange={event => setQuery(event.target.value)}
          aria-invalid={invalid || undefined}
          describedBy={describedBy}
          className="min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-16 text-gray-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
        />
        {selectedOption && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            disabled={disabled}
            className="absolute right-2 top-6 min-h-11 rounded px-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
            onClick={() => {
              selectedOnClose.current = true;
              setQuery('');
              onChange('');
            }}
          >
            Clear
          </button>
        )}
        <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg empty:invisible dark:border-gray-700 dark:bg-gray-800">
          {visible.length === 0 ? (
            <div role="status" aria-live="polite" className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No players match
            </div>
          ) : (
            visible.map(option => (
              <ComboboxOption
                key={option.id}
                value={option}
                className="cursor-pointer select-none rounded px-3 py-2 text-sm text-gray-900 data-[focus]:bg-primary-100 data-[selected]:font-semibold dark:text-gray-100 dark:data-[focus]:bg-primary-900/40"
              >
                {option.label}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default SearchableCombobox;
